/**
 * Dual-Axis Combo Chart Type Definition
 * Bars on primary (left) y-axis + line on secondary (right) y-axis
 */

import {
  titleControl,
  gridControl,
  xLabelControl,
  labelRotationControl,
  truncateXAxisLabelsControl,
} from '../shared/controls';

import {
  matplotlibSetup,
  createFigure,
  titleCode,
  finishCode,
} from '../shared/codeSnippets';

import {
  seabornSetup,
  seabornCreateFigure,
  seabornTitleCode,
  seabornFinishCode,
} from '../shared/seabornSnippets';

import { SINGLE_COLORS } from '../shared/palettes';
import { getComboColorPair } from '../shared/palettes';
import {
  SERIES_LINE_STYLES_MPL,
  SERIES_MARKERS_MPL,
  SERIES_HATCHES_MPL,
} from '../../utils/accessibility';

// Sample data: model accuracy (bars) vs inference speed (line)
// Illustrates the accuracy–speed tradeoff across LLM providers
const SAMPLE_DATA = [
  { name: 'Item A', barValue: 92.3, lineValue: 1247 },
  { name: 'Item B', barValue: 91.5, lineValue: 892  },
  { name: 'Item C', barValue: 93.1, lineValue: 743  },
  { name: 'Item D', barValue: 88.4, lineValue: 1583 },
  { name: 'Item E', barValue: 85.2, lineValue: 2341 },
  { name: 'Item F', barValue: 87.1, lineValue: 1892 },
];

const COLOR_OPTIONS = SINGLE_COLORS.map(c => c.value);

const combo = {
  id: 'combo',
  name: 'Combo Chart',
  category: 'combo',
  description: 'Bars and line on dual y-axes for comparing two metrics',

  getSampleData: () => SAMPLE_DATA,
  sampleData: SAMPLE_DATA,

  defaultConfig: {
    barColor: '#4C6EE6',
    lineColor: '#C44B3D',
    lineWidth: 2,
    showMarkers: true,
    useSeriesStyles: false,
    usePatternFills: false,
    showGrid: true,
    xlabel: 'Category',
    leftLabel: 'Value A',
    rightLabel: 'Value B',
    title: '',
    labelRotation: null,
    truncateXAxisLabels: false,
    outputFormat: 'matplotlib',
  },

  controls: [
    {
      key: 'barColor',
      label: 'Bar Color',
      type: 'color',
      options: COLOR_OPTIONS,
    },
    {
      key: 'lineColor',
      label: 'Line Color',
      type: 'color',
      options: COLOR_OPTIONS,
    },
    {
      key: 'lineWidth',
      label: 'Line Width',
      type: 'slider',
      min: 1,
      max: 4,
      step: 0.5,
      unit: 'px',
    },
    {
      key: 'useSeriesStyles',
      label: 'Use Non-Color Series Styles',
      type: 'toggle',
    },
    {
      key: 'usePatternFills',
      label: 'Use Pattern Fills',
      type: 'toggle',
    },
    {
      key: 'showMarkers',
      label: 'Show Markers on Line',
      type: 'toggle',
    },
    gridControl,
    labelRotationControl,
    truncateXAxisLabelsControl,
    xLabelControl,
    {
      key: 'leftLabel',
      label: 'Left Y-axis Label',
      type: 'text',
      placeholder: 'Enter left y-axis label',
    },
    {
      key: 'rightLabel',
      label: 'Right Y-axis Label',
      type: 'text',
      placeholder: 'Enter right y-axis label',
    },
    titleControl,
  ],

  generateCode: (config, dataContext, s) => {
    const hasUserData = dataContext?.isLoaded &&
      dataContext.mapping?.category &&
      dataContext.mapping?.barValue &&
      dataContext.mapping?.lineValue;

    const colorPair = getComboColorPair(config, s);
    const barColor = colorPair.primary;
    const lineColor = colorPair.secondary;
    const lineWidth = config.lineWidth || 2;
    const markerSize = 50;
    const lineStyles = JSON.stringify(SERIES_LINE_STYLES_MPL);
    const markerStyles = JSON.stringify(SERIES_MARKERS_MPL);
    const hatchPatterns = JSON.stringify(SERIES_HATCHES_MPL);
    const useSeriesStyles = !!config.useSeriesStyles;
    const usePatternFills = !!config.usePatternFills;

    const categories = SAMPLE_DATA.map(d => d.name);
    const barValues = SAMPLE_DATA.map(d => d.barValue);
    const lineValues = SAMPLE_DATA.map(d => d.lineValue);

    const dataSection = hasUserData
      ? `df = pd.read_csv('${dataContext.fileName}')
categories = df['${dataContext.mapping.category}'].tolist()
bar_values = df['${dataContext.mapping.barValue}'].tolist()
line_values = df['${dataContext.mapping.lineValue}'].tolist()`
      : `categories = ${JSON.stringify(categories)}
bar_values = [${barValues.join(', ')}]
line_values = [${lineValues.join(', ')}]`;

    const markerCode = config.showMarkers
      ? `ax2.scatter(categories, line_values, color='${lineColor}', s=${markerSize}, zorder=4, edgecolors='none', marker=marker_style)`
      : '# Markers disabled';

    const gridCode = config.showGrid
      ? `ax.grid(axis='y', alpha=${s?.gridAlpha || 0.3}, color='${s?.gridColor || '#CCCCCC'}', linestyle='--', linewidth=0.5, zorder=0)\nax.set_axisbelow(True)`
      : '# Grid disabled';

    if (config.outputFormat === 'seaborn') {
      return `${seabornSetup(s)}
import pandas as pd

# ======== DATA ========
${dataSection}
# ======================
${seabornCreateFigure(7, 5, 300, s)}
line_styles = ${lineStyles}
marker_styles = ${markerStyles}
hatch_patterns = ${hatchPatterns}
line_style = line_styles[0] if ${useSeriesStyles ? 'True' : 'False'} else '-'
marker_style = marker_styles[0] if ${useSeriesStyles ? 'True' : 'False'} else 'o'
hatch_pattern = hatch_patterns[0] if ${usePatternFills ? 'True' : 'False'} else ''

# Seaborn does not support dual-axis natively; using matplotlib directly
# Bar chart on primary (left) y-axis
bars = ax.bar(categories, bar_values, color='${barColor}', width=0.6, zorder=2, edgecolor='none', hatch=hatch_pattern)

# Line chart on secondary (right) y-axis
ax2 = ax.twinx()
ax2.plot(categories, line_values, color='${lineColor}', linewidth=${lineWidth}, linestyle=line_style, zorder=3)
${markerCode}

# Style axes
ax.set_xlabel('${config.xlabel || ''}', fontsize=${s?.labelSize || 12}, labelpad=10, color='${s?.labelColor || '#000000'}')
ax.set_ylabel('${config.leftLabel || ''}', fontsize=${s?.labelSize || 12}, color='${barColor}', labelpad=10)
ax2.set_ylabel('${config.rightLabel || ''}', fontsize=${s?.labelSize || 12}, color='${lineColor}', labelpad=10)
ax.tick_params(axis='y', labelcolor='${barColor}')
ax2.tick_params(axis='y', labelcolor='${lineColor}')
plt.xticks(rotation=30, ha='right')

${gridCode}

# Legend that reflects hatch and line style/marker
from matplotlib.patches import Patch
from matplotlib.lines import Line2D
legend_handles = [
    Patch(facecolor='${barColor}', edgecolor='none', hatch=hatch_pattern, label='${config.leftLabel || 'Bars'}'),
    Line2D([0], [0], color='${lineColor}', linewidth=${lineWidth}, linestyle=line_style,
           marker=(marker_style if ${config.showMarkers ? 'True' : 'False'} else None),
           label='${config.rightLabel || 'Line'}')
]
ax.legend(handles=legend_handles, loc='upper right', frameon=False, fontsize=${s?.legendSize || 10})

# Remove top spine only (keep all others for dual-axis clarity)
ax.spines['top'].set_visible(False)
ax2.spines['top'].set_visible(False)
${seabornTitleCode(config, s)}
${seabornFinishCode('combo_chart')}`;
    }

    // Matplotlib output (default)
    return `${matplotlibSetup(s)}
import pandas as pd

# ======== DATA ========
${dataSection}
# ======================
${createFigure(7, 5, 300, s)}
line_styles = ${lineStyles}
marker_styles = ${markerStyles}
hatch_patterns = ${hatchPatterns}
line_style = line_styles[0] if ${useSeriesStyles ? 'True' : 'False'} else '-'
marker_style = marker_styles[0] if ${useSeriesStyles ? 'True' : 'False'} else 'o'
hatch_pattern = hatch_patterns[0] if ${usePatternFills ? 'True' : 'False'} else ''

# Bar chart on primary (left) y-axis
bars = ax.bar(categories, bar_values, color='${barColor}', width=0.6, zorder=2, edgecolor='none', hatch=hatch_pattern)

# Line chart on secondary (right) y-axis
ax2 = ax.twinx()
ax2.plot(categories, line_values, color='${lineColor}', linewidth=${lineWidth}, linestyle=line_style, zorder=3)
${markerCode}

# Style axes
ax.set_xlabel('${config.xlabel || ''}', fontsize=${s?.labelSize || 12}, labelpad=10, color='${s?.labelColor || '#000000'}')
ax.set_ylabel('${config.leftLabel || ''}', fontsize=${s?.labelSize || 12}, color='${barColor}', labelpad=10)
ax2.set_ylabel('${config.rightLabel || ''}', fontsize=${s?.labelSize || 12}, color='${lineColor}', labelpad=10)
ax.tick_params(axis='y', labelcolor='${barColor}')
ax2.tick_params(axis='y', labelcolor='${lineColor}')
plt.xticks(rotation=30, ha='right')

${gridCode}

# Legend that reflects hatch and line style/marker
from matplotlib.patches import Patch
from matplotlib.lines import Line2D
legend_handles = [
    Patch(facecolor='${barColor}', edgecolor='none', hatch=hatch_pattern, label='${config.leftLabel || 'Bars'}'),
    Line2D([0], [0], color='${lineColor}', linewidth=${lineWidth}, linestyle=line_style,
           marker=(marker_style if ${config.showMarkers ? 'True' : 'False'} else None),
           label='${config.rightLabel || 'Line'}')
]
ax.legend(handles=legend_handles, loc='upper right', frameon=False, fontsize=${s?.legendSize || 10})

# Remove top spine only
ax.spines['top'].set_visible(False)
ax2.spines['top'].set_visible(False)
ax.spines['bottom'].set_linewidth(${s?.spineWidth || 1})
ax.spines['left'].set_linewidth(${s?.spineWidth || 1})
ax2.spines['right'].set_linewidth(${s?.spineWidth || 1})
${titleCode(config, s)}
${finishCode('combo_chart')}`;
  },
};

export default combo;
